/**
 * Link di amministrazione, divisi fra Admin · Lega e Admin · App (Andrea
 * 2026-09-29). Unica fonte per il drawer mobile e per la pagina Gestione
 * (desktop), cosi' le due viste non vanno piu' fuori sincrono.
 *
 * - Admin · Lega: quello che decide la singola lega (stagioni, regolamento,
 *   utenti, calcolo e modifica manuale delle giornate, asta, scommesse...).
 *   Lo vedono presidente e admin della lega corrente.
 * - Admin · App: i dati comuni a tutte le leghe (foglio voti, probabili,
 *   listone, valori TM), Discord e la manutenzione. Solo l'amministratore
 *   dell'app (`is_admin_app`), anche lato server (require_admin_app).
 */

import type { User } from "../../lib/auth-store";

export interface AdminLink {
  to: string;
  label: string;
  icon: string;
  /** Riga breve sotto il titolo nelle card della pagina Gestione. */
  subtitle: string;
  /** Ruoli di lega che vedono il link (primario o aggiuntivi). */
  roles?: string[];
}

const PL_ADMIN = ["presidente_lega", "admin"];

export const ADMIN_LEGA_LINKS: AdminLink[] = [
  {
    to: "/giornata?tab=calcola",
    label: "Calcolo giornata",
    icon: "🧮",
    subtitle: "Calcola, ricalcola, riapri",
    roles: PL_ADMIN,
  },
  {
    to: "/admin/stagioni",
    label: "Stagioni",
    icon: "📆",
    subtitle: "Chiudi / crea nuova",
    roles: PL_ADMIN,
  },
  {
    to: "/admin/competizioni",
    label: "Competizioni",
    icon: "🏟",
    subtitle: "Calendario + gironi CL",
    roles: PL_ADMIN,
  },
  {
    to: "/admin/regolamento",
    label: "Editor regolamento",
    icon: "📝",
    subtitle: "Bonus/malus + soglie",
    roles: PL_ADMIN,
  },
  {
    to: "/admin/inviti",
    label: "Inviti utenti",
    icon: "✉️",
    subtitle: "Invita partecipanti",
    roles: PL_ADMIN,
  },
  {
    to: "/admin/users",
    label: "Gestione utenti",
    icon: "👤",
    subtitle: "Ruoli + reset password",
    roles: PL_ADMIN,
  },
  {
    to: "/tesoreria",
    label: "Tesoreria",
    icon: "🏦",
    subtitle: "Quote e premi",
    roles: [...PL_ADMIN, "tesoriere"],
  },
  {
    to: "/admin/asta",
    label: "Assegna / svincola giocatori",
    icon: "🔨",
    subtitle: "Asta, correzioni rose e valori",
    roles: [...PL_ADMIN, "mod_mercato"],
  },
  {
    to: "/admin/scommesse",
    label: "Gestione Squad Bet",
    icon: "🎰",
    subtitle: "Partite di cartello + quote",
    roles: PL_ADMIN,
  },
  {
    to: "/admin/rinvii",
    label: "Rinvii partite",
    icon: "⏸",
    subtitle: "6 politico",
    roles: PL_ADMIN,
  },
  {
    to: "/admin/audit",
    label: "Audit log",
    icon: "🔍",
    subtitle: "Storico operazioni",
    roles: PL_ADMIN,
  },
];

export const ADMIN_APP_LINKS: AdminLink[] = [
  {
    to: "/admin/leghe-monitor",
    label: "Monitor leghe",
    icon: "🛰",
    subtitle: "Tutte le leghe, stato e intervento",
  },
  {
    to: "/admin/squadcup",
    label: "SquadCup",
    icon: "🦅",
    subtitle: "La coppa fra i vincitori delle leghe",
  },
  {
    to: "/admin/carica-voti",
    label: "Carica voti",
    icon: "📊",
    subtitle: "Foglio voti per tutte le leghe",
  },
  {
    to: "/admin/voti-giornata",
    label: "Voti caricati",
    icon: "📋",
    subtitle: "Elenco per giornata, correzioni singole",
  },
  {
    to: "/admin/probabili",
    label: "Probabili formazioni",
    icon: "❓",
    subtitle: "Scraping + override, per tutte le leghe",
  },
  {
    to: "/admin/config-discord",
    label: "Config Discord",
    icon: "💬",
    subtitle: "Webhook + eventi",
  },
  {
    to: "/admin/icone-giocatori",
    label: "Icone giocatori",
    icon: "🎨",
    subtitle: "Generatore + assegnazione",
  },
  {
    to: "/admin/import-listone",
    label: "Import listone",
    icon: "📥",
    subtitle: "Listone comune",
  },
  {
    to: "/admin/tm-import",
    label: "Import Transfermarkt",
    icon: "💎",
    subtitle: "Valori di mercato",
  },
  {
    to: "/admin/backup",
    label: "Backup",
    icon: "💾",
    subtitle: "Ultimi run + manuale",
  },
];

export function ruoliUtente(user: User | null | undefined): Set<string> {
  if (!user) return new Set();
  return new Set<string>([user.ruolo_lega ?? "", ...(user.ruoli_aggiuntivi ?? [])]);
}

/** Link di lega: serve uno dei ruoli. Link d'app: admin dell'app, oppure uno
 * dei ruoli indicati (es. moderatore TM per l'import Transfermarkt). */
export function linkLegaVisibile(link: AdminLink, user: User | null | undefined): boolean {
  if (!user) return false;
  const ruoli = ruoliUtente(user);
  return !link.roles || link.roles.some((r) => ruoli.has(r));
}

export function linkAppVisibile(link: AdminLink, user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.is_admin_app) return true;
  const ruoli = ruoliUtente(user);
  return !!link.roles && link.roles.some((r) => ruoli.has(r));
}

export function adminLegaVisibili(user: User | null | undefined): AdminLink[] {
  return ADMIN_LEGA_LINKS.filter((l) => linkLegaVisibile(l, user));
}

export function adminAppVisibili(user: User | null | undefined): AdminLink[] {
  return ADMIN_APP_LINKS.filter((l) => linkAppVisibile(l, user));
}
